import { Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { type UsageMetric, getResidentsUsageSummary } from '@/lib/api/residents';

interface MetricCardProps {
  label: string;
  metric?: UsageMetric;
  stub?: boolean;
  stubLabel?: string;
}

function MetricCard({ label, metric, stub, stubLabel }: MetricCardProps) {
  return (
    <Card sx={{ flex: 1, minWidth: 180 }} variant="outlined">
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        {stub ? (
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            {stubLabel ?? '연동 준비 중'}
          </Typography>
        ) : metric ? (
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="h4" component="span">
              {metric.used}
              <Typography component="span" variant="body1" color="text.secondary">
                {' / '}
                {metric.total}
              </Typography>
            </Typography>
            <Typography
              variant="body2"
              color={
                metric.rate >= 80
                  ? 'success.main'
                  : metric.rate >= 50
                    ? 'warning.main'
                    : 'error.main'
              }
              sx={{ ml: 'auto' }}
            >
              {metric.rate.toFixed(1)}%
            </Typography>
          </Stack>
        ) : (
          <Skeleton variant="text" width={120} height={40} />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * 의뢰서 5-1 상단 '앱별 사용 현황 요약 바'.
 * 입소자(ADMITTED + RESIDENT) 전체 대비 오늘 BGT 사용 인원/비율.
 * 보이스웰니스는 stub.
 */
export function UsageSummaryBar() {
  const { data, isLoading } = useQuery({
    queryKey: ['residents', 'usage-summary'],
    queryFn: getResidentsUsageSummary,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <MetricCard label="BGT 사용 현황" metric={!isLoading ? data?.bgt : undefined} />
      <MetricCard
        label="보이스웰니스"
        metric={!isLoading ? (data?.voiceWellness ?? undefined) : undefined}
      />
    </Stack>
  );
}
